import React, { useEffect, useState, useMemo } from 'react';
import { FaChevronLeft, FaChevronRight, FaXmark } from 'react-icons/fa6';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { messageI18n } from '../../../../../helpers/i18n/toastI18n';
import cn from 'classnames';

import { Modal } from '../../../../ComponnentsUI/common/Modal';
import {
  CATEGORIES,
  OPACITY,
} from '../../../../../../../../ui/src/components/MeasurementTable/Components/Report/config/planes.config';
import { isMeasurementAllowed } from '../../../../../constants/measurementLabels';

// Helper function to format titles
const formatTitle = (text: string = ''): string => {
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to get color for quality flags based on label/code
function getColorForLabel(label: string) {
  const labelLower = label.toLowerCase();

  const matchedCategory = CATEGORIES.find((category) => {
    const categoryIdLower = category.id.toLowerCase();

    return (
      categoryIdLower.includes(labelLower) ||
      labelLower.includes(categoryIdLower)
    );
  });

  if (matchedCategory) {
    return matchedCategory.color;
  }

  return `rgba(128, 128, 128, ${OPACITY})`;
}

const autoSelectTopPrediction = (predictionHistory = []) =>
  predictionHistory.map((frame, index) => ({
    ...frame,
    selectedForReport: index === 0,
  }));

function SelectView({
  selectedPlan,
  setSelectedPlansModal,
  tag,
  globalState,
  setGlobalState,
  studyUID,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEditingFlags, setIsEditingFlags] = useState(false);
  const [hiddenCriteria, setHiddenCriteria] = useState<Set<string>>(new Set());

  const toggleCriteriaVisibility = (flagCode: string) => {
    setHiddenCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(flagCode)) next.delete(flagCode);
      else next.add(flagCode);
      return next;
    });
  };

  const predictionHistory = useMemo(
    () => globalState?.planes?.[tag]?.predictionHistory || [],
    [globalState, tag]
  );
  const currentPrediction = useMemo(
    () => predictionHistory[currentIndex],
    [predictionHistory, currentIndex]
  );

  const segColorMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (currentPrediction?.structuresToDraw || []).forEach(
      (seg: { label?: string; color?: string }) => {
        if (!seg.color) { return; }
        const cleanCode = (seg.label || '').replace(/_not_visible$/, '');
        if (cleanCode && !map[cleanCode]) {
          map[cleanCode] = seg.color;
        }
      }
    );
    return map;
  }, [currentPrediction]);

  // Reset index when the selected plan changes or when the modal opens
  useEffect(() => {
    setCurrentIndex(0);
  }, [selectedPlan]);

  useEffect(() => {
    if (!selectedPlan || !tag || !globalState?.pixelSpacingMap) return;

    const viewData = globalState?.planes?.[tag];
    if (!viewData?.predictionHistory) return;

    let hasUpdates = false;
    const updatedHistory = viewData.predictionHistory.map((frame) => {
      if (!frame.correlation_id || !frame.measurements) return frame;

      if (frame.measurements.unit === 'cm') return frame;

      const pixelSpacingData = globalState.pixelSpacingMap[frame.correlation_id];
      const estimatedMmPerPixel = pixelSpacingData?.estimated_mm_per_pixel;

      if (estimatedMmPerPixel === null || estimatedMmPerPixel === undefined) return frame;

      hasUpdates = true;
      const calibratedMeasurements: Record<string, number | string> = { unit: 'cm' };
      
      Object.entries(frame.measurements).forEach(([key, value]) => {
        if (key === 'unit') return;
        const valueInPx = Number(value);
        const valueInMm = valueInPx * estimatedMmPerPixel;
        const valueInCm = valueInMm;
        calibratedMeasurements[key] = valueInCm;
      });

      return {
        ...frame,
        measurements: calibratedMeasurements,
      };
    });

    if (hasUpdates) {
      setGlobalState((prev) => ({
        ...prev,
        planes: {
          ...prev.planes,
          [tag]: {
            ...prev.planes[tag],
            predictionHistory: updatedHistory,
            imgReport:
              updatedHistory[0]?.imagePredictedPlane ??
              prev.planes[tag]?.imgReport,
          },
        },
      }));
    }
  }, [selectedPlan, tag, globalState?.pixelSpacingMap]);

  const handleDeleteFrame = () => {
    if (!currentPrediction || predictionHistory.length === 0) {
      return;
    }

    const updatedHistory = autoSelectTopPrediction(
      predictionHistory.filter((_, index) => index !== currentIndex)
    );

    if (updatedHistory.length === 0) {
      setGlobalState((prev) => {
        const newPlanes = { ...prev.planes };
        delete newPlanes[tag];
        return {
          ...prev,
          planes: newPlanes,
        };
      });
      setSelectedPlansModal('');
      messageI18n.info('realtime.viewUndetected');
    } else {
      setGlobalState((prev) => ({
        ...prev,
        planes: {
          ...prev.planes,
          [tag]: {
            ...prev.planes[tag],
            predictionHistory: updatedHistory,
            imgReport:
              updatedHistory[0]?.imagePredictedPlane ??
              prev.planes[tag]?.imgReport,
          },
        },
      }));

      // Adjust current index if needed
      if (currentIndex >= updatedHistory.length) {
        setCurrentIndex(updatedHistory.length - 1);
      }
      messageI18n.success('realtime.frameRemoved');
    }
  };

  const handleProceedAsView = () => {
    if (!currentPrediction || predictionHistory.length === 0) {
      return;
    }

    const updatedHistory = predictionHistory.map((frame) => ({
      ...frame,
      selectedForReport: false,
    }));
    updatedHistory[currentIndex] = {
      ...updatedHistory[currentIndex],
      selectedForReport: true,
    };

    setGlobalState((prev) => ({
      ...prev,
      planes: {
        ...prev.planes,
        [tag]: {
          ...prev.planes[tag],
          predictionHistory: updatedHistory,
          imgReport:
            updatedHistory[currentIndex]?.imagePredictedPlane ??
            prev.planes[tag]?.imgReport,
        },
      },
    }));

    messageI18n.success('realtime.viewMarkedForReport');
  };

  const measurements = useMemo(() => {
    const results: {
      name: string;
      value: string;
      valueInCm?: number;
      unit: string;
      hasPixelSpacing: boolean;
      pixelSpacingStatus: 'calibrated' | 'failed' | 'pending';
    }[] = [];
    if (!currentPrediction) return results;

    const measurementUnit = currentPrediction.measurements?.unit;
    const isAlreadyCalibrated = measurementUnit === 'cm';

    // Look up pixel spacing from the pixelSpacingMap using correlation_id
    const correlationId = currentPrediction.correlation_id;
    const pixelSpacingData = correlationId ? globalState?.pixelSpacingMap?.[correlationId] : null;
    const estimatedMmPerPixel = pixelSpacingData?.estimated_mm_per_pixel;
    const hasValidPixelSpacing = estimatedMmPerPixel !== null && estimatedMmPerPixel !== undefined;
    
    // Determine pixel spacing status:

    const pixelSpacingStatus: 'calibrated' | 'failed' | 'pending' = 
      isAlreadyCalibrated || hasValidPixelSpacing
        ? 'calibrated'
        : (pixelSpacingData ? 'failed' : 'pending');

    if (
      currentPrediction.measurements &&
      typeof currentPrediction.measurements === 'object'
    ) {
      Object.entries(currentPrediction.measurements).forEach(([key, value]) => {
        if (key === 'unit') return;
        if (key.startsWith('MEAS__') && !isMeasurementAllowed(key)) {
          return;
        }

        const numericValue = Number(value);
        
        if (isAlreadyCalibrated) {
          results.push({
            name: key.replace(/^MEAS__/, ''),
            value: `${numericValue.toFixed(2)} cm`,
            valueInCm: numericValue,
            unit: 'cm',
            hasPixelSpacing: true,
            pixelSpacingStatus: 'calibrated',
          });
        } else if (hasValidPixelSpacing) {
          const valueInMm = numericValue * estimatedMmPerPixel;
          const valueInCm = valueInMm / 10;
          results.push({
            name: key.replace(/^MEAS__/, ''),
            value: `${numericValue.toFixed(2)} px → ${valueInCm.toFixed(2)} cm`,
            valueInCm: valueInCm,
            unit: 'cm',
            hasPixelSpacing: true,
            pixelSpacingStatus: 'calibrated',
          });
        } else {
          results.push({
            name: key.replace(/^MEAS__/, ''),
            value: `${numericValue.toFixed(2)} px`,
            unit: 'px',
            hasPixelSpacing: false,
            pixelSpacingStatus,
          });
        }
      });
    }
    
    return results;
  }, [currentPrediction, globalState?.pixelSpacingMap]);

  const showMeasurementsSection = useMemo(() => {
    const relevantPlanes = [
      'Fetal Abdominal Standard Plane',
      'Transthalamic View',
      'Transcerebellar View',
      'Femur (Femoral) Plane',
    ];
    return relevantPlanes.includes(selectedPlan);
  }, [selectedPlan]);

  return (
    <Modal
      isOpen={!!selectedPlan}
      onClose={() => setSelectedPlansModal('')}
      size="5xl"
      className="bg-card-color-200 dark:bg-gray-900 !max-h-[92vh] flex flex-col"
    >
      {/* Modal Header */}
      <div className="flex-shrink-0 px-6 py-3 border-b border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">
              {selectedPlan}
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {predictionHistory.length}{' '}
              {predictionHistory.length === 1 ? 'view' : 'views'} detected
            </p>
          </div>
          <button
            onClick={() => setSelectedPlansModal('')}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
            aria-label="Close"
          >
            <FaXmark size={20} />
          </button>
        </div>
      </div>

      {/* Modal Content */}
      <div className="flex-1 min-h-0 overflow-hidden p-4">
        {predictionHistory.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-4 h-full">
            {/* Left Side: Image Viewer */}
            <div className="lg:col-span-4 flex flex-col gap-3 h-full min-h-0">
              {/* Image Display - Takes up calculated space */}
              <div
                className="relative bg-black dark:bg-gray-950 rounded-lg border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden flex items-center justify-center"
                style={{ height: 'calc(100% - 110px)' }}
              >
                <img
                  src={currentPrediction?.imagePredictedPlane}
                  className="max-w-full max-h-full object-contain"
                  alt={`View ${currentIndex + 1} of ${selectedPlan}`}
                />
                <div className="absolute top-3 left-3 bg-gray-800/90 dark:bg-gray-900/90 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-lg font-medium shadow-lg">
                  View {currentIndex + 1} of {predictionHistory.length}
                </div>
                {currentPrediction?.selectedForReport && (
                  <div className="absolute top-3 right-3 bg-green-500 text-white text-sm px-3 py-1.5 rounded-lg font-bold shadow-lg flex items-center gap-2">
                    ✓ Selected for Report
                  </div>
                )}
              </div>

              {/* Thumbnail Navigation - Fixed height */}
              <div className="flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700 p-2 shadow-sm h-[100px]">
                <div className="relative flex items-center justify-center px-10 h-full">
                  <button
                    onClick={() =>
                      setCurrentIndex((prev) =>
                        prev > 0 ? prev - 1 : predictionHistory.length - 1
                      )
                    }
                    className="absolute left-0 z-10 rounded-full bg-gray-800 dark:bg-gray-700 p-2 text-white shadow-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                  >
                    <FaChevronLeft size={14} />
                  </button>
                  <div className="flex items-center gap-2 overflow-x-auto h-full py-1 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600">
                    {predictionHistory.map((view, index) => (
                      <div
                        key={view.frame_id || index}
                        onClick={() => setCurrentIndex(index)}
                        className={cn(
                          'relative rounded-lg cursor-pointer transition-all duration-200 flex-shrink-0 overflow-hidden',
                          'border-2 h-[80px]',
                          currentIndex === index
                            ? 'border-blue-500 dark:border-blue-400 scale-105 shadow-md'
                            : 'border-slate-200 dark:border-gray-600 opacity-60 hover:opacity-100 hover:border-blue-300 dark:hover:border-blue-500'
                        )}
                      >
                        <img
                          src={view.imagePredictedPlane}
                          alt={`Thumbnail ${index + 1}`}
                          className="h-full w-auto object-cover"
                        />
                        <div className="absolute bottom-0.5 right-0.5 bg-gray-800/90 dark:bg-gray-900/90 backdrop-blur-sm text-white text-xs px-1.5 py-0.5 rounded font-medium">
                          {index + 1}
                        </div>
                        {view.selectedForReport && (
                          <div className="absolute top-0.5 right-0.5 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold shadow-lg">
                            ✓
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentIndex((prev) =>
                        prev < predictionHistory.length - 1 ? prev + 1 : 0
                      )
                    }
                    className="absolute right-0 z-10 rounded-full bg-gray-800 dark:bg-gray-700 p-2 text-white shadow-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                  >
                    <FaChevronRight size={14} />
                  </button>
                </div>
              </div>
            </div>

            {/* Right Side: Information Panel */}
            <div className="lg:col-span-3 flex flex-col h-full min-h-0 bg-white dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden">
              {/* Quality Criteria Header */}
              <div className="flex-shrink-0 p-3 border-b border-slate-200 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <h2 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
                    Quality Criteria
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsEditingFlags(!isEditingFlags)}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      isEditingFlags
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {isEditingFlags ? 'Done' : 'Edit'}
                  </button>
                  <div className="text-orange-500 text-lg">⚡</div>
                </div>
              </div>

              {/* Measurements Section */}
              {measurements.length > 0 && (
                <div className="flex-shrink-0 border-b border-slate-200 dark:border-gray-700 p-3">
                  <div className="border rounded-lg p-3 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wide">
                        Measurements
                      </div>
                      {!measurements[0]?.hasPixelSpacing && (
                        <div className="flex items-center gap-1">
                          <AlertCircle size={14} className={
                            measurements[0]?.pixelSpacingStatus === 'failed'
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-yellow-600 dark:text-yellow-400'
                          } />
                          <span className={`text-xs font-medium ${
                            measurements[0]?.pixelSpacingStatus === 'failed'
                              ? 'text-red-700 dark:text-red-400'
                              : 'text-yellow-700 dark:text-yellow-400'
                          }`}>
                            {measurements[0]?.pixelSpacingStatus === 'failed' 
                              ? 'Calibration failed' 
                              : 'Pending calibration'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      {measurements.map((meas, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between items-center"
                        >
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {meas.name}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${
                              !meas.hasPixelSpacing 
                                ? 'text-yellow-600 dark:text-yellow-400' 
                                : 'text-blue-700 dark:text-blue-400'
                            }`}>
                              {meas.value}
                            </span>
                            {!meas.hasPixelSpacing && (
                              <AlertCircle size={14} className="text-yellow-600 dark:text-yellow-400" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Quality Criteria List - Flexible height with scroll */}
              <div className="flex-1 min-h-0 p-4 overflow-y-auto space-y-3">
                {currentPrediction?.quality_criteria?.length > 0 ? (
                  currentPrediction.quality_criteria.map((criteria, idx) => {
                    // Map the criteria to match QualityFlagData structure
                    const flag = {
                      flag_code: criteria.title,
                      display_name: formatTitle(criteria.title),
                      value: criteria.bool,
                    };

                    const isNotVisibleType = flag.display_name
                      ?.toLowerCase()
                      .includes('not visible');


                    const isStructureActive = isNotVisibleType
                      ? !flag.value
                      : flag.value;

                    const cleanName = flag.display_name
                      .replace(/\s*not\s*visible\s*/gi, '')
                      .trim();
                    const isHidden = hiddenCriteria.has(cleanName);

                    const cleanFlagCode = (flag.flag_code || '').replace(
                      /_not_visible$/,
                      ''
                    );
                    const structureColor =
                      segColorMap[cleanFlagCode] ?? getColorForLabel(cleanFlagCode);

                    return (
                      <div
                        key={idx}
                        className={`border border-slate-200 dark:border-gray-700 rounded-lg p-3 flex justify-between items-center ${
                          isEditingFlags ? 'bg-blue-50' : 'bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {/* Eye Icon for visibility toggle */}
                          <button
                            disabled={!isStructureActive}
                            onClick={() => toggleCriteriaVisibility(cleanName)}
                            className={`p-1 rounded-md transition-colors ${
                              !isStructureActive
                                ? 'text-gray-200 cursor-not-allowed'
                                : isHidden
                                  ? 'text-gray-400 hover:bg-gray-200'
                                  : 'text-blue-500 hover:bg-blue-100'
                            }`}
                            title={
                              !isStructureActive
                                ? 'No structure to hide'
                                : isHidden
                                  ? 'Show Label'
                                  : 'Hide Label'
                            }
                          >
                            {isHidden || !isStructureActive ? (
                              <EyeOff size={16} />
                            ) : (
                              <Eye size={16} />
                            )}
                          </button>

                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor: isStructureActive
                                ? structureColor
                                : '#9ca3af',
                            }}
                          ></div>
                          <p
                            className={`text-sm ${!isStructureActive ? 'text-gray-400' : 'text-gray-800'} dark:text-gray-200`}
                          >
                            {flag.display_name}
                          </p>
                        </div>

                        {/* Toggle Switch */}
                        <button
                          onClick={() => {
                            if (isEditingFlags) {
                              const updatedCriteria = [
                                ...currentPrediction.quality_criteria,
                              ];
                              updatedCriteria[idx] = {
                                ...criteria,
                                bool: !criteria.bool,
                              };

                              const updatedHistory = [...predictionHistory];
                              updatedHistory[currentIndex] = {
                                ...currentPrediction,
                                quality_criteria: updatedCriteria,
                              };

                              setGlobalState((prev) => ({
                                ...prev,
                                planes: {
                                  ...prev.planes,
                                  [tag]: {
                                    ...prev.planes[tag],
                                    predictionHistory: updatedHistory,
                                  },
                                },
                              }));
                            }
                          }}
                          disabled={!isEditingFlags}
                          className={`w-10 h-5 rounded-full relative transition-colors duration-200 ${
                            flag.value ? 'bg-blue-500' : 'bg-gray-300'
                          } ${!isEditingFlags ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                        >
                          <div
                            className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform duration-200 absolute top-0.5 ${
                              flag.value ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          ></div>
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-sm text-gray-500 pt-10">
                    No quality criteria for this frame.
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex-shrink-0 p-3 border-t border-slate-200 dark:border-gray-700 flex gap-2">
                <button
                  onClick={handleProceedAsView}
                  className={`flex-1 px-3 py-1.5 rounded-lg font-medium text-xs transition-all duration-200 flex items-center justify-center gap-1.5 ${
                    currentPrediction?.selectedForReport
                      ? 'bg-gray-200 dark:bg-gray-600 cursor-not-allowed text-gray-500 dark:text-gray-400'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                  disabled={currentPrediction?.selectedForReport}
                >
                  {currentPrediction?.selectedForReport ? 'In Report' : 'Proceed'}
                </button>
                <button
                  onClick={handleDeleteFrame}
                  className="px-2.5 py-1.5 rounded-lg font-medium text-xs transition-all duration-200 bg-red-100 hover:bg-red-200 text-red-700 hover:text-red-800 border border-red-200 hover:border-red-300"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">
                📭
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                No views available for this plane.
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default SelectView;
